import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { sessionApi } from '@/entities/session'
import { clearOnboardingSkipped } from '@/entities/setup'
import { queryKeys } from '@/shared/api/queryKeys'
import type { Account, LoginRequest, RegisterRequest } from '@/shared/api/types'

export function useLogin() {
  const queryClient = useQueryClient()
  const navigate = useNavigate()

  return useMutation({
    mutationFn: (body: LoginRequest) => sessionApi.login(body),
    onSuccess: (account: Account) => {
      // Ответ входа кладём в кэш сразу: иначе следующий экран сначала
      // сходит за /me и на это время покажет заглушку — при том, что
      // данные уже пришли этим самым запросом.
      queryClient.setQueryData(queryKeys.account, account)
      navigate('/', { replace: true })
    },
  })
}

export function useRegister() {
  const queryClient = useQueryClient()
  const navigate = useNavigate()

  return useMutation({
    mutationFn: (body: RegisterRequest) => sessionApi.register(body),
    onSuccess: (account: Account) => {
      queryClient.setQueryData(queryKeys.account, account)
      // «Настроить позже» прошлого аккаунта на этом браузере не должно
      // открывать кабинет новому.
      clearOnboardingSkipped()
      // Сразу в настройку: пустой проект — это агент, который на
      // звонке не знает, что продаёт. Обзор с прочерками оставил бы
      // человека гадать, что делать дальше, а настройщик просто
      // спросит.
      navigate('/onboarding', { replace: true })
    },
  })
}

export function useLogout() {
  const queryClient = useQueryClient()
  const navigate = useNavigate()

  return useMutation({
    mutationFn: () => sessionApi.logout(),
    // onSettled, а не onSuccess: если запрос не дошёл, сессию всё равно
    // надо забыть локально. Человек нажал «Выйти» — на чужом ноутбуке
    // остаться в кабинете он не должен ни при каком ответе сервера.
    onSettled: () => {
      queryClient.clear()
      navigate('/login', { replace: true })
    },
  })
}
